<template>
    <AlertDialog :open="isOpen" @update:open="handleOpenChange">
        <AlertDialogContent class="max-w-md">
            <AlertDialogHeader>
                <AlertDialogTitle>{{ title }}</AlertDialogTitle>
            </AlertDialogHeader>

            <div class="space-y-4">
                <!-- Icon based on type -->
                <div class="flex justify-center">
                    <div :class="iconClasses">
                        <component :is="iconComponent" :class="iconSizeClasses" />
                    </div>
                </div>

                <!-- Message -->
                <div v-if="message" class="text-center [&_ul]:list-disc [&_ul]:list-inside [&_ul]:text-left [&_ul]:space-y-1 [&_ul]:mt-2 [&_li]:ml-0" v-html="sanitizeHtml(message)"></div>

                <!-- Input requirement for sensitive actions -->
                <div v-if="requiresInput" class="space-y-2 mt-6">
                    <Label :for="inputId"><span v-html="sanitizeHtml(inputLabel)" /></Label>
                    <Input :id="inputId" v-model="inputValue" :placeholder="inputPlaceholder" class="mt-2" />
                </div>
            </div>

            <AlertDialogFooter>
                <AlertDialogCancel @click="onCancel">
                    {{ cancelText }}
                </AlertDialogCancel>
                <AlertDialogAction @click="onConfirm" :disabled="requiresInput && inputValue !== expectedInput" :class="buttonVariantClasses">
                    {{ confirmText }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { AlertTriangle, Trash2, Save, Pause, Play, Archive, AlertCircle } from 'lucide-vue-next';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { sanitizeHtml } from '@/utils/utils';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        default: 'confirm', // 'delete', 'confirm', 'warning', 'save', 'pause', 'snapshot'
        validator: (value) => ['delete', 'confirm', 'warning', 'save', 'pause', 'snapshot', 'archive'].includes(value)
    },
    title: {
        type: String,
        default: 'Confirm Action'
    },
    message: {
        type: String,
        default: ''
    },
    confirmText: {
        type: String,
        default: 'Confirm'
    },
    cancelText: {
        type: String,
        default: 'Cancel'
    },
    requiresInput: {
        type: Boolean,
        default: false
    },
    inputPlaceholder: {
        type: String,
        default: ''
    },
    inputLabel: {
        type: String,
        default: 'To confirm, please type:'
    },
    expectedInput: {
        type: String,
        default: 'delete me'
    },
    // Legacy support for useConfirmBeforeAction props
    entity: String,
    name: String,
    actionName: String
});

const emit = defineEmits(['confirm', 'cancel', 'update:open']);

const inputValue = ref('');
const inputId = 'confirm-input';

// Clear input when dialog closes (any close method) to prevent persistence
watch(
    () => props.isOpen,
    (isOpen, oldIsOpen) => {
        if (oldIsOpen && !isOpen) {
            // Dialog just closed - clear input for next time
            inputValue.value = '';
        }
    }
);

// Computed properties for styling based on type
const iconComponent = computed(() => {
    switch (props.type) {
        case 'delete':
            return Trash2;
        case 'save':
            return Save;
        case 'pause':
            return Pause;
        case 'snapshot':
            return Play;
        case 'archive':
            return Archive;
        case 'warning':
            return AlertTriangle;
        default:
            return AlertCircle;
    }
});

const iconClasses = computed(() => {
    const baseClasses = 'mx-auto flex h-12 w-12 items-center justify-center rounded-full';
    switch (props.type) {
        case 'delete':
            return `${baseClasses} bg-destructive/10`;
        case 'warning':
            return `${baseClasses} bg-destructive/10`;
        case 'save':
        case 'snapshot':
            return `${baseClasses} bg-accent/10`;
        case 'pause':
        case 'archive':
            return `${baseClasses} bg-destructive/10`;
        default:
            return `${baseClasses} bg-primary/10`;
    }
});

const iconSizeClasses = computed(() => {
    switch (props.type) {
        case 'delete':
            return 'h-6 w-6 text-destructive';
        case 'warning':
            return 'h-6 w-6 text-destructive';
        case 'save':
        case 'snapshot':
            return 'h-6 w-6 text-accent-foreground';
        case 'pause':
        case 'archive':
            return 'h-6 w-6 text-destructive';
        default:
            return 'h-6 w-6 text-primary';
    }
});

const buttonVariantClasses = computed(() => {
    switch (props.type) {
        case 'delete':
            return 'bg-destructive hover:bg-destructive/90 text-destructive-foreground';
        case 'warning':
            return 'bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-700';
        default:
            return '';
    }
});

// Auto-generate title and message based on legacy props
const title = computed(() => {
    if (props.title !== 'Confirm Action') return props.title;

    const action = props.actionName || props.type;
    const entityText = props.entity ? `${props.entity} ` : '';

    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${entityText}`;
});

const message = computed(() => {
    if (props.message) return props.message;

    // Auto-generate message for legacy useConfirmBeforeAction compatibility
    const action = props.actionName || props.type;
    const entityText = props.entity || 'item';
    const nameText = props.name ? `<strong>${props.name}</strong>` : 'this item';

    if (props.type === 'delete') {
        return `You are about to delete ${entityText} '${nameText}'`;
    }

    return `You are about to ${action} ${nameText}`;
});

const inputLabel = computed(() => {
    if (props.inputLabel !== 'To confirm, please type:') return props.inputLabel;

    const expectedText = props.expectedInput;
    return `To confirm, please input '<strong>${expectedText}</strong>' below.`;
});

const handleOpenChange = (open) => {
    // Don't auto-cancel when dialog closes - let explicit actions handle this
};

const onConfirm = () => {
    emit('confirm', true);
};

const onCancel = () => {
    emit('cancel', false);
};

const handleKeydown = (event) => {
    if (!props.isOpen) return;

    if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
    } else if (event.key === 'Enter') {
        const inputValid = !props.requiresInput || inputValue.value === props.expectedInput;
        if (inputValid) {
            event.preventDefault();
            onConfirm();
        }
    }
};
onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>
